import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Menu, ChevronRight } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';

const NavigationManager = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [message, setMessage] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    order: 0,
    show_in_header: true,
    show_in_footer: true,
    parent_id: null
  });

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/navigation-links`);
      if (response.ok) {
        const data = await response.json();
        setLinks(data);
      }
    } catch (error) {
      console.error('Failed to load navigation links:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!formData.title || !formData.url) {
      setMessage('Please provide both title and URL');
      return;
    }

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/navigation-links`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setMessage('✅ Navigation link added successfully!');
        setFormData({ title: '', url: '', order: 0, show_in_header: true, show_in_footer: true, parent_id: null });
        setAddingNew(false);
        fetchLinks();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage(`❌ ${error.detail || 'Failed to add link'}`);
      }
    } catch (error) {
      console.error('Add error:', error);
      setMessage(`❌ Error: ${error.message}`);
    }
  };

  const handleUpdate = async (linkId) => {
    const link = links.find(l => l.id === linkId);
    
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/navigation-links/${linkId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: link.title,
          url: link.url,
          order: link.order,
          show_in_header: link.show_in_header,
          show_in_footer: link.show_in_footer,
          parent_id: link.parent_id
        })
      });

      if (response.ok) {
        setMessage('✅ Link updated successfully!');
        setEditing(null);
        fetchLinks();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage(`❌ ${error.detail || 'Failed to update link'}`);
      }
    } catch (error) {
      console.error('Update error:', error);
      setMessage(`❌ Error: ${error.message}`);
    }
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this navigation link?')) return;

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/navigation-links/${linkId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setMessage('✅ Link deleted successfully!');
        fetchLinks();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage(`❌ ${error.detail || 'Failed to delete link'}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      setMessage(`❌ Error: ${error.message}`);
    }
  };

  const updateLink = (linkId, field, value) => {
    setLinks(links.map(l => l.id === linkId ? { ...l, [field]: value } : l));
  };

  if (loading) {
    return <div className="flex justify-center p-8">Loading navigation links...</div>;
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Navigation Links"
        description="Manage header and footer navigation"
        icon={<Menu className="h-6 w-6" />}
        actions={
          <Button onClick={() => setAddingNew(!addingNew)} variant="adminPrimary">
            <Plus className="h-4 w-4 mr-2" />
            Add Link
          </Button>
        }
      />

      {message && (
        <div className={`p-4 rounded ${
          message.includes('✅') ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' :
          'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          {message}
        </div>
      )}

      {addingNew && (
        <Card className="p-6 bg-gray-50 dark:bg-gray-800 border-2 border-red-200 dark:border-red-800">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100 flex items-center">
            <Plus className="h-5 w-5 mr-2 text-red-600" />
            Add New Navigation Link
          </h3>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., About Us"
              />
            </div>
            <div>
              <Label>URL</Label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="e.g., /about or /#programs"
              />
            </div>
            <div>
              <Label>Order</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <Label>Parent Item (Optional)</Label>
              <Select
                value={formData.parent_id || "none"}
                onValueChange={(value) =>
                  setFormData({ ...formData, parent_id: value === "none" ? null : value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Top-level item (no parent)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Top-level item (no parent)</SelectItem>
                  {links
                    .filter(link => !link.parent_id)
                    .sort((a, b) => a.order - b.order)
                    .map(link => (
                      <SelectItem key={link.id} value={link.id}>
                        {link.title}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Select a parent to create a dropdown menu item
              </p>
            </div>
            <div className="flex gap-6">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.show_in_header}
                  onCheckedChange={(checked) => setFormData({ ...formData, show_in_header: checked })}
                />
                <Label>Show in Header</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.show_in_footer}
                  onCheckedChange={(checked) => setFormData({ ...formData, show_in_footer: checked })}
                />
                <Label>Show in Footer</Label>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAdd} variant="adminPrimary">
                <Save className="h-4 w-4 mr-2" />
                Save Link
              </Button>
              <Button onClick={() => setAddingNew(false)} variant="adminOutline">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      <div className="space-y-3">
        {links
          .filter(link => !link.parent_id)
          .sort((a, b) => a.order - b.order)
          .map((link) => {
            const childLinks = links
              .filter(l => l.parent_id === link.id)
              .sort((a, b) => a.order - b.order);

            return (
              <div key={link.id}>
                <Card className={`p-4 ${link.is_system ? 'border-2 border-blue-400 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/30' : ''}`}>
            {editing === link.id ? (
              <div className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    value={link.title}
                    onChange={(e) => updateLink(link.id, 'title', e.target.value)}
                  />
                </div>
                <div>
                  <Label>URL</Label>
                  <Input
                    value={link.url}
                    onChange={(e) => updateLink(link.id, 'url', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Order</Label>
                  <Input
                    type="number"
                    value={link.order}
                    onChange={(e) => updateLink(link.id, 'order', parseInt(e.target.value))}
                  />
                </div>
                <div>
                  <Label>Parent Item (Optional)</Label>
                  <Select
                    value={link.parent_id || "none"}
                    onValueChange={(value) =>
                      updateLink(link.id, 'parent_id', value === "none" ? null : value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Top-level item (no parent)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Top-level item (no parent)</SelectItem>
                      {links
                        .filter(l => !l.parent_id && l.id !== link.id)
                        .sort((a, b) => a.order - b.order)
                        .map(l => (
                          <SelectItem key={l.id} value={l.id}>
                            {l.title}
                          </SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                  {links.filter(l => l.parent_id === link.id).length > 0 && (
                    <p className="text-xs text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded p-2 mt-1">
                      ⚠️ This is a parent item with {links.filter(l => l.parent_id === link.id).length} children.
                      Cannot make it a child of another item.
                    </p>
                  )}
                </div>
                <div className="flex gap-6">
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={link.show_in_header}
                      onCheckedChange={(checked) => updateLink(link.id, 'show_in_header', checked)}
                    />
                    <Label>Show in Header</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={link.show_in_footer}
                      onCheckedChange={(checked) => updateLink(link.id, 'show_in_footer', checked)}
                    />
                    <Label>Show in Footer</Label>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => handleUpdate(link.id)} variant="adminPrimary">
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                  <Button onClick={() => setEditing(null)} variant="adminOutline">
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <Menu className="h-5 w-5 text-gray-400" />
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{link.title}</h3>
                      {link.is_system && (
                        <span className="px-2 py-1 bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-xs font-semibold rounded">
                          SYSTEM
                        </span>
                      )}
                      {childLinks.length > 0 && (
                        <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 text-xs font-semibold rounded">
                          PARENT ({childLinks.length})
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{link.url}</p>
                    <div className="flex gap-3 mt-1">
                      <span className={`text-xs ${link.show_in_header ? 'text-green-600' : 'text-gray-400'}`}>
                        {link.show_in_header ? '✓' : '✗'} Header
                      </span>
                      <span className={`text-xs ${link.show_in_footer ? 'text-green-600' : 'text-gray-400'}`}>
                        {link.show_in_footer ? '✓' : '✗'} Footer
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Order: {link.order}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(link.id)}>
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  {!link.is_system && (
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(link.id)}>
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Card>

          {/* Render child links */}
          {childLinks.length > 0 && (
            <div className="ml-12 mt-2 space-y-2 border-l-2 border-gray-300 dark:border-gray-600 pl-4">
              {childLinks.map((childLink) => (
                <Card key={childLink.id} className="p-3 bg-gray-50 dark:bg-gray-800">
                  {editing === childLink.id ? (
                    <div className="space-y-4">
                      <div>
                        <Label>Title</Label>
                        <Input
                          value={childLink.title}
                          onChange={(e) => updateLink(childLink.id, 'title', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>URL</Label>
                        <Input
                          value={childLink.url}
                          onChange={(e) => updateLink(childLink.id, 'url', e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Order</Label>
                        <Input
                          type="number"
                          value={childLink.order}
                          onChange={(e) => updateLink(childLink.id, 'order', parseInt(e.target.value))}
                        />
                      </div>
                      <div>
                        <Label>Parent Item</Label>
                        <Select
                          value={childLink.parent_id || "none"}
                          onValueChange={(value) =>
                            updateLink(childLink.id, 'parent_id', value === "none" ? null : value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Top-level item (no parent)</SelectItem>
                            {links
                              .filter(l => !l.parent_id && l.id !== childLink.id)
                              .sort((a, b) => a.order - b.order)
                              .map(l => (
                                <SelectItem key={l.id} value={l.id}>
                                  {l.title}
                                </SelectItem>
                              ))
                            }
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-6">
                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={childLink.show_in_header}
                            onCheckedChange={(checked) => updateLink(childLink.id, 'show_in_header', checked)}
                          />
                          <Label>Show in Header</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Switch
                            checked={childLink.show_in_footer}
                            onCheckedChange={(checked) => updateLink(childLink.id, 'show_in_footer', checked)}
                          />
                          <Label>Show in Footer</Label>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => handleUpdate(childLink.id)} variant="adminPrimary" size="sm">
                          <Save className="h-4 w-4 mr-2" />
                          Save
                        </Button>
                        <Button onClick={() => setEditing(null)} variant="outline" size="sm">
                          <X className="h-4 w-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-1">
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                        <div>
                          <h4 className="text-sm font-medium">{childLink.title}</h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400">{childLink.url}</p>
                          <div className="flex gap-2 mt-1">
                            <span className={`text-xs ${childLink.show_in_header ? 'text-green-600' : 'text-gray-400'}`}>
                              {childLink.show_in_header ? '✓' : '✗'} Header
                            </span>
                            <span className={`text-xs ${childLink.show_in_footer ? 'text-green-600' : 'text-gray-400'}`}>
                              {childLink.show_in_footer ? '✓' : '✗'} Footer
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">Order: {childLink.order}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditing(childLink.id)}>
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(childLink.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      );
    })}

        {links.length === 0 && (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <Menu className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-300">No navigation links yet</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Add your first link to get started</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NavigationManager;
